import { type AiTagId, type SiteId } from '@solinkcorp/api-types';
import { useMemo } from 'react';
import usePeopleTrackingRawData from './usePeopleTrackingRawData';

export enum VISION_ANALYTICS_CONFIGURATION {
  ZONE = 'ZONE',
  LINE = 'LINE',
  BOTH = 'BOTH',
}

export interface VisionAnalyticsConfig {
  configurationType: VISION_ANALYTICS_CONFIGURATION;
  zoneOrLineIds: string[];
}

/**
 * @param siteId - The site ID to fetch vision analytics cameras for
 * @returns Record mapping aiTagIds to their configuration type and associated zone IDs
 */
export default function useSiteSpecificVisionAnalyticsCamera(
  siteId: SiteId | string | undefined,
): Record<AiTagId, VisionAnalyticsConfig> {
  const { rawData } = usePeopleTrackingRawData(siteId);

  // Extract unique aiTagIds from event_lines and dwell_event_polygons
  const aiTagConfigurationsMap = useMemo(() => {
    if (!rawData) {
      return {};
    }

    const aiTagIdMap = new Map<
      string,
      { hasLine: boolean; hasZone: boolean; zoneOrLineIds: string[] }
    >();

    // Iterate through each camera in rawData
    // Note: rawData contains additional fields (event_lines) not in the strict VMS types

    for (const cameraConfig of Object.values(rawData)) {
      // Process event_lines (LINE configuration)
      for (const item of cameraConfig['event_lines'] ?? []) {
        const zoneIds = item?.id;
        for (const id of item?.metadata?.aiTagIds ?? []) {
          const existing = aiTagIdMap.get(id) ?? {
            hasLine: false,
            hasZone: false,
            zoneOrLineIds: [],
          };
          const zoneOrLineIds =
            zoneIds && !existing.zoneOrLineIds.includes(zoneIds)
              ? [...existing.zoneOrLineIds, zoneIds]
              : existing.zoneOrLineIds;
          aiTagIdMap.set(id, { ...existing, hasLine: true, zoneOrLineIds });
        }
      }

      // Process dwell_event_polygons (ZONE configuration)
      for (const item of cameraConfig['dwell_event_polygons'] ?? []) {
        const lineIds = item?.metadata?.polygonId;
        for (const id of item?.metadata?.aiTagIds ?? []) {
          const existing = aiTagIdMap.get(id) ?? {
            hasLine: false,
            hasZone: false,
            zoneOrLineIds: [],
          };
          const zoneOrLineIds =
            lineIds && !existing.zoneOrLineIds.includes(lineIds)
              ? [...existing.zoneOrLineIds, lineIds]
              : existing.zoneOrLineIds;
          aiTagIdMap.set(id, { ...existing, hasZone: true, zoneOrLineIds });
        }
      }
    }

    // Convert map to record with appropriate enum values and zone IDs
    const result: Record<AiTagId, VisionAnalyticsConfig> = {};
    for (const [id, config] of aiTagIdMap.entries()) {
      let configurationType: VISION_ANALYTICS_CONFIGURATION;
      if (config.hasLine && config.hasZone) {
        configurationType = VISION_ANALYTICS_CONFIGURATION.BOTH;
      } else if (config.hasLine) {
        configurationType = VISION_ANALYTICS_CONFIGURATION.LINE;
      } else if (config.hasZone) {
        configurationType = VISION_ANALYTICS_CONFIGURATION.ZONE;
      } else {
        continue; // Skip if neither line nor zone (shouldn't happen)
      }

      result[id as AiTagId] = {
        configurationType,
        zoneOrLineIds: config.zoneOrLineIds,
      };
    }

    return result;
  }, [rawData]);

  return aiTagConfigurationsMap;
}
